import type { ReactNode } from 'react'
import { Loader2Icon, PaletteIcon, SparklesIcon } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import type { MoodboardGate } from './use-moodboard-gate'

type Props = {
  gate: MoodboardGate
  onOpenMoodboard: (id: number) => void
  onCreateMoodboard: () => void
  isCreating: boolean
}

/** Tells the user why a post would not (yet) sound like their brand, with the
 *  one action that fixes it. Renders nothing when the moodboard is usable. */
export function HomeMoodboardNotice({
  gate,
  onOpenMoodboard,
  onCreateMoodboard,
  isCreating,
}: Props) {
  if (gate.kind === 'ready' || gate.kind === 'loading') return null

  const content = describe(gate, onOpenMoodboard, onCreateMoodboard)

  return (
    <div
      role="status"
      className="bg-card text-foreground mt-4 flex items-start gap-3 rounded-2xl px-4 py-3 shadow-sm">
      <div className="mt-0.5 shrink-0">{content.icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{content.title}</p>
        <p className="text-muted-foreground mt-0.5 text-sm">{content.body}</p>
      </div>
      {content.action && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 rounded-full"
          disabled={isCreating}
          onClick={content.action.onClick}>
          {isCreating ? <Loader2Icon size={14} className="animate-spin" /> : content.action.label}
        </Button>
      )}
    </div>
  )
}

type Content = {
  icon: ReactNode
  title: string
  body: string
  action: { label: string; onClick: () => void } | null
}

function describe(
  gate: Exclude<MoodboardGate, { kind: 'ready' } | { kind: 'loading' }>,
  onOpenMoodboard: (id: number) => void,
  onCreateMoodboard: () => void,
): Content {
  switch (gate.kind) {
    case 'analyzing':
      return {
        icon: <Loader2Icon size={16} className="text-primary animate-spin" />,
        title: 'CASSI is still reading your moodboard',
        body: 'Your brand brief is being generated so your post sounds like you. This usually takes about a minute. You can start typing in the meantime.',
        action: null,
      }
    case 'stalled':
      return {
        icon: <SparklesIcon size={16} className="text-primary" />,
        title: 'Your brand brief is taking longer than expected',
        body: 'Open your moodboard to run the analysis again. You can also write a post without it.',
        action: { label: 'Open moodboard', onClick: () => onOpenMoodboard(gate.moodboardId) },
      }
    case 'no-brief':
      return {
        icon: <SparklesIcon size={16} className="text-primary" />,
        title: "Your moodboard doesn't have a brand brief yet",
        body: 'Once CASSI has analyzed your moodboard, your posts are written in your voice.',
        action: { label: 'Open moodboard', onClick: () => onOpenMoodboard(gate.moodboardId) },
      }
    case 'none':
      return {
        icon: <PaletteIcon size={16} className="text-primary" />,
        title: 'Show CASSI your brand first',
        body: 'Add your website, a few notes and images to a moodboard. CASSI uses it to write posts that sound like you.',
        action: { label: 'Create moodboard', onClick: onCreateMoodboard },
      }
  }
}
